import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { LogTimeCell } from "../cells/LogTimeCell";
import { HistoryDetailsEntityCell } from "../cells/HistoryDetailsEntityCell";
import { ReportSettingsActivityTextCell } from "./ReportSettingsActivityTextCell";
import { ReportTemplateActivityCell } from "./ReportTemplateActivityCell";
import {
    ReportSettingsActivityRow,
    ReportSettingsActivityTableKind,
} from "./reportSettingsActivityRows";

export enum ReportSettingsActivityDetailsColumn {
    TIME = "time",
    ENTITY = "entity",
    REPORT = "report",
    SETTING = "setting",
    PREVIOUS = "previous",
    PREVIOUS_SOURCE = "previousSource",
    MODIFIED = "modified",
    MODIFIED_SOURCE = "modifiedSource",
}

export interface ReportSettingsActivityDetailsView extends View {
    type: ViewType.HISTORY_UPDATED_TRANSACTION;
    columns: Array<{ id: ReportSettingsActivityDetailsColumn }>;
}

const SETTINGS_COLUMNS = [
    ReportSettingsActivityDetailsColumn.TIME,
    ReportSettingsActivityDetailsColumn.REPORT,
    ReportSettingsActivityDetailsColumn.SETTING,
    ReportSettingsActivityDetailsColumn.ENTITY,
    ReportSettingsActivityDetailsColumn.PREVIOUS,
    ReportSettingsActivityDetailsColumn.MODIFIED,
    ReportSettingsActivityDetailsColumn.PREVIOUS_SOURCE,
    ReportSettingsActivityDetailsColumn.MODIFIED_SOURCE,
];

const TEMPLATE_COLUMNS = [
    ReportSettingsActivityDetailsColumn.TIME,
    ReportSettingsActivityDetailsColumn.ENTITY,
    ReportSettingsActivityDetailsColumn.PREVIOUS,
    ReportSettingsActivityDetailsColumn.MODIFIED,
];

export const createReportSettingsActivityDetailsView = (
    kind: ReportSettingsActivityTableKind,
): ReportSettingsActivityDetailsView => ({
    type: ViewType.HISTORY_UPDATED_TRANSACTION,
    columns: (kind === ReportSettingsActivityTableKind.SETTINGS
        ? SETTINGS_COLUMNS
        : TEMPLATE_COLUMNS
    ).map((id) => ({ id })),
    filters: {},
    options: { density: ViewDensity.MD },
});

const COLUMN_NAMES: Record<ReportSettingsActivityDetailsColumn, string> = {
    [ReportSettingsActivityDetailsColumn.TIME]: "Time",
    [ReportSettingsActivityDetailsColumn.ENTITY]: "Entity",
    [ReportSettingsActivityDetailsColumn.REPORT]: "Report",
    [ReportSettingsActivityDetailsColumn.SETTING]: "Field",
    [ReportSettingsActivityDetailsColumn.PREVIOUS]: "Previous",
    [ReportSettingsActivityDetailsColumn.PREVIOUS_SOURCE]: "Previous source",
    [ReportSettingsActivityDetailsColumn.MODIFIED]: "Modified",
    [ReportSettingsActivityDetailsColumn.MODIFIED_SOURCE]: "Modified source",
};

const textColumn = (id: ReportSettingsActivityDetailsColumn) => {
    const name = COLUMN_NAMES[id];
    return {
        name,
        header: name,
        accessorKey: id,
        cell: ReportSettingsActivityTextCell,
        minSize: 140,
        maxSize: 320,
    };
};

export const createReportSettingsActivityDetailsViewConfig = (
    kind: ReportSettingsActivityTableKind,
): ViewConfig<
    ReportSettingsActivityRow,
    ReportSettingsActivityDetailsColumn,
    never
> => {
    const previous = textColumn(ReportSettingsActivityDetailsColumn.PREVIOUS);
    const modified = textColumn(ReportSettingsActivityDetailsColumn.MODIFIED);
    return {
        columns: {
            [ReportSettingsActivityDetailsColumn.TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                ...DATE_COLUMN_SIZE,
            },
            [ReportSettingsActivityDetailsColumn.ENTITY]: {
                ...textColumn(ReportSettingsActivityDetailsColumn.ENTITY),
                cell: HistoryDetailsEntityCell,
                accessorKey: "entityId",
                minSize: 80,
                maxSize: 160,
            },
            [ReportSettingsActivityDetailsColumn.REPORT]: textColumn(
                ReportSettingsActivityDetailsColumn.REPORT,
            ),
            [ReportSettingsActivityDetailsColumn.SETTING]: textColumn(
                ReportSettingsActivityDetailsColumn.SETTING,
            ),
            [ReportSettingsActivityDetailsColumn.PREVIOUS]:
                kind === ReportSettingsActivityTableKind.TEMPLATE
                    ? { ...previous, cell: ReportTemplateActivityCell }
                    : previous,
            [ReportSettingsActivityDetailsColumn.MODIFIED]:
                kind === ReportSettingsActivityTableKind.TEMPLATE
                    ? { ...modified, cell: ReportTemplateActivityCell }
                    : modified,
            [ReportSettingsActivityDetailsColumn.PREVIOUS_SOURCE]: textColumn(
                ReportSettingsActivityDetailsColumn.PREVIOUS_SOURCE,
            ),
            [ReportSettingsActivityDetailsColumn.MODIFIED_SOURCE]: textColumn(
                ReportSettingsActivityDetailsColumn.MODIFIED_SOURCE,
            ),
        },
    };
};
